<template>
  <section id="admin-settings-search-summary" class="height-100-p">
    <el-form :model="editForm" :rules="editFormRules" ref="editForm" @submit.native.prevent>
      <el-table
        :data="adminSettings.list"
        size="mini"
        tooltip-effect="dark"
        sortable
        class="dark-blue-table"
        :empty-text="$t('common.notAbleToFindRecords')"
        @sort-change="handleSortChange"
        stripe
        v-loading="adminSettings.loadingSearch"
      >
        <el-table-column
            prop="name"
            :label="$t('admin.settings.name')"
            :min-width="4"
            align="left"
            header-align="center"
            sortable="custom"
        >
          <template slot-scope="scope">
            <el-tooltip
                :content="scope.row.name"
                class="item"
                effect="dark"
                placement="top-start"
            >
              <span>{{ scope.row.name }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column
          prop="description"
          :label="$t('admin.settings.description')"
          :min-width="8"
          align="left"
          header-align="center"
          sortable="custom"
        >
          <template slot-scope="scope">
            <el-tooltip
              :content="scope.row.description"
              class="item"
              effect="dark"
              placement="top-start"
            >
              <span>{{ scope.row.description }}</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column
          prop="value"
          :label="$t('admin.settings.value')"
          :min-width="4"
          align="center"
          sortable="custom"
        >
          <template slot-scope="scope">
            <template v-if="scope.row.edit">
              <el-form-item prop="value">
                <el-input
                  v-if="isText(scope.row.type)"
                  v-alphanumeric-validation
                  v-model="editForm.value"
                  class="inline-input"
                  size="mini"
                  ref="value"
                  :clearable="scope.row.allowNull"
                ></el-input>
                <el-input
                  v-if="isNumber(scope.row.type)"
                  v-numeric-validation
                  v-model="editForm.value"
                  class="inline-input"
                  size="mini"
                  :clearable="scope.row.allowNull"
                ></el-input>
                <el-checkbox
                  v-if="isBoolean(scope.row.type)"
                  v-model="editForm.value"
                  class="inline-input"
                  size="mini"
                ></el-checkbox>
                <el-time-picker
                  v-if="isTime(scope.row.type)"
                  v-model="editForm.value"
                  class="inline-input"
                  size="mini"
                  format="HH:mm"
                  style="padding-left: 30px !important;"
                ></el-time-picker>
              </el-form-item>
            </template>

            <template v-else>
              <el-tooltip
                :content="scope.row.value.toString()"
                class="item"
                effect="dark"
                placement="top-start"
              >
                <span>{{ scope.row.value }}</span>
              </el-tooltip>
            </template>
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('common.options')"
          :min-width="1"
          align="center"
        >
          <template slot-scope="scope" v-if="$can(permissions.ADMIN_SETTINGS.EDIT_SETTING)">
            <el-tooltip v-if="!adminSettings.isEditingRow" class="item" effect="dark" :enterable="false" :content="$t('common.edit')" placement="top-start">
              <i class="el-icon-edit icon-action" @click="handleEdit(scope.row)"></i>
            </el-tooltip>
            <template v-else-if="adminSettings.isEditingRow && scope.row.edit">
              <el-button icon="el-icon-success" circle size="mini" @click="handleUpdate(scope.row)"></el-button>
              <el-button icon="el-icon-circle-close" circle size="mini" @click="cancelEdit(scope.row)"></el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-footer" v-show="adminSettings.total > 0">
        <span class="el-pagination__total">
          <span class="el-pagination__title">{{ $t('admin.settings.totalLabel') }}</span>
             {{ adminSettings.total }}
        </span>
        <el-pagination
          @size-change="handleSearchChangeLimit"
          :page-sizes="sizes"
          :page-size="adminSettings.actualFilters.rows"
          :current-page.sync="adminSettings.actualFilters.page"
          @current-change="handleSearchChangePage"
          :total="adminSettings.total"
          layout="sizes, prev, pager, next, jumper"
        ></el-pagination>
      </div>
    </el-form>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { Message } from 'element-ui';
import constants from '@/utils/constants';
import { ADMIN_SETTINGS_SEARCH, ADMIN_SETTINGS_SET_EDITING_ROW, ADMIN_SETTINGS_UPDATE } from '@/store/actions';

export default {
  name: 'AdminSettingsSummarySection',
  computed: {
    ...mapGetters(['adminSettings']),
    sizes() {
      return constants.TABLES.DEFAULT_LIMIT_SIZES;
    },
    settingType() {
      return constants.ADMIN_SETTINGS.TYPES;
    }
  },
  data() {
    const validateValue = (rule, value, callback) => {
      if (this.isNumber(this.currentEditingRow.type)
          && !this.currentEditingRow.allowNull && !this.currentEditingRow.allowEmpty
          && (value === undefined || value === null)) {
        callback(new Error(this.$t('admin.settings.errors.valueRequired')));
      } else if (this.currentEditingRow.type === constants.ADMIN_SETTINGS.TYPES.STRING) {
        if (!this.currentEditingRow.allowNull && (value === undefined || value === null)) {
          callback(new Error(this.$t('admin.settings.errors.valueRequired')));
        } else if (!this.currentEditingRow.allowEmpty && value.toString().trim() === '') {
          callback(new Error(this.$t('admin.settings.errors.blankValueInvalid')));
        } else if (value.toString().length > this.currentEditingRow.maxLength) {
          callback(new Error(this.$t('admin.settings.errors.maxLengthInvalid').replace('[maLength]', this.currentEditingRow.maxLength)));
        }
      } else if (this.currentEditingRow.type === constants.ADMIN_SETTINGS.TYPES.EMAIL) {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (!this.currentEditingRow.allowNull && (value === undefined || value === null)) {
          callback(new Error(this.$t('admin.settings.errors.valueRequired')));
        } else if (!this.currentEditingRow.allowEmpty && !re.test(value)) {
          callback(new Error(this.$t('admin.settings.errors.emailInvalid')));
        }
      } else if (this.currentEditingRow.type === constants.ADMIN_SETTINGS.TYPES.LIST) {
        const re = /^\w+(,\w+)*$/;
        if (!this.currentEditingRow.allowNull && (value === undefined || value === null)) {
          callback(new Error(this.$t('admin.settings.errors.valueRequired')));
        } else if (!this.currentEditingRow.allowEmpty && !re.test(value)) {
          callback(new Error(this.$t('admin.settings.errors.listInvalid')));
        }
      }

      callback();
    };

    return {
      currentEditingRow: null,
      editForm: {
        id: null,
        name: null,
        value: null
      },
      editFormRules: {
        value: [
          { validator: validateValue, trigger: 'blur' }
        ]
      }
    };
  },
  methods: {
    ...mapActions({
      search: ADMIN_SETTINGS_SEARCH,
      setEditingRow: ADMIN_SETTINGS_SET_EDITING_ROW,
      update: ADMIN_SETTINGS_UPDATE
    }),
    handleSortChange({ prop, order }) {
      this.search({ orderField: prop, orderDirection: constants.TABLES.ORDER_DIRECTION[order] });
    },
    handleSearchChangeLimit(data) {
      this.search({ rows: data, page: constants.TABLES.DEFAULT_PAGE });
    },
    handleSearchChangePage(val) {
      this.search({ page: val });
    },
    handleEdit(row) {
      this.currentEditingRow = row;
      this.setEditingRow(true);
      row.edit = true;
      this.editForm.id = row.id;
      this.editForm.name = row.name;
      if (this.isTime(row.type)) {
        this.editForm.value = new Date(`1970-01-01T${row.value}`);
      } else {
        this.editForm.value = row.value;
      }
    },
    handleUpdate(row) {
      if (this.isBoolean(this.currentEditingRow.type)) {
        this.editForm.value = Boolean(this.editForm.value);
      }
      if (this.isTime(this.currentEditingRow.type)) {
        this.editForm.value = this.editForm.value.toTimeString().substring(0, 5);
      }

      this.$refs.editForm.validate(async (valid, validationsErrors) => {
        if (valid) {
          Message.closeAll();
          const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
          this.update(this.editForm).then((response) => {
            this.$message.success(response.message);
            this.search({});
            this.cancelEdit(row);
          }).finally(() => {
            loading.close();
          });
        } else {
          let errorMessage = '';
          Object.keys(validationsErrors).forEach((key) => {
            validationsErrors[key].forEach((validation) => {
              errorMessage += `${validation.message}. `;
            });
          });
          this.$message.warning(errorMessage);
        }
      });
    },
    cancelEdit(row) {
      this.$refs.editForm.resetFields();
      row.edit = false;
      this.setEditingRow(false);
      this.currentEditingRow = null;
    },
    isText(type) {
      return type === this.settingType.STRING || type === this.settingType.LIST || type === this.settingType.EMAIL;
    },
    isNumber(type) {
      return type === this.settingType.DOUBLE || type === this.settingType.INTEGER;
    },
    isBoolean(type) {
      return type === this.settingType.BOOLEAN;
    },
    isTime(type) {
      return type === this.settingType.TIME;
    }
  }
};
</script>
